import { Fragment, useState, type ReactNode } from 'react';
import {
  Building2,
  CalendarDays,
  Flag,
  LayoutGrid,
  MessageSquare,
  Search,
  Trophy,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { INTL_TAG, useLocale, useT, type MessageKey } from '../../i18n';
import type { CapabilityInfo, RoleView } from '../../lib/api/platformRoles';
import {
  areasFor,
  groupOf,
  levelStrength,
  standingIn,
  toneOf,
  withLevel,
  type Area,
  type AreaStanding,
  type RoleGroup,
} from './roleAreas';

// ═══════════════════════════════════════════════════════════════════
// RETTIGHETER — THE OVERVIEW.
//
// 2026-10-19 (owner 1A/2A/3A): roles down the side, a FIXED set of areas
// across the top, the LEVEL in each cell; it grows downwards as roles are
// added and never sideways. Clicking a cell opens that role's area under the
// row and saves through the SAME draft and save path the grid uses
// (`PermissionMatrixSection`), so there is one way a role changes.
//
// 2026-10-20 (owner 1A/2A/3A, second round): «vi må ha bedre grafikk, litt
// farger … jeg liker ikke helt de knappene på toppen, man mister litt
// sammenhengen». So:
//   1A  ONE PAGE, no tabs: the organization roles are grouped «For både senter
//       og klubb», «Bare for senter», «Bare for klubb», all visible at once;
//   2A  every area has its own colour and icon, and a cell is stronger the
//       higher the level, with ●○○ … ●●● saying the same without colour;
//   3A  no view switch at the top: a click on a cell opens that area, a click
//       on the role's name opens ALL its rights (the grid, for that one role).
// Roles stay rows on purpose: there are more of them than areas, and they are
// what grows.
//
// It decides nothing. A level is a name for a set of capabilities
// (`roleAreas.ts`); the server stores capabilities and refuses what it would
// refuse from the grid.
//
// Born migrated: every string is a catalog key. Capability titles and
// descriptions, and role names, are DATA handed in by the caller.
// ═══════════════════════════════════════════════════════════════════

export interface RoleAreaOverviewProps {
  roles: RoleView[];
  /** The organization capabilities a role may hold. */
  rows: CapabilityInfo[];
  /** A short name for a capability; the caller owns the vocabulary. */
  titleOf: (capability: CapabilityInfo) => string;
  canWrite: boolean;
  busyRole: string | null;
  rowValue: (role: RoleView) => string[];
  isDirty: (role: RoleView) => boolean;
  pendingChange: (role: RoleView) => { added: string[]; removed: string[] };
  /** Replace a role's draft capabilities. */
  onSetCapabilities: (role: RoleView, capabilities: string[]) => void;
  onToggle: (role: RoleView, capability: string) => void;
  onSave: (role: RoleView) => void;
  /**
   * EVERYTHING ABOUT ONE ROLE (3A): the caller's full grid for this role —
   * every right, rename, availability, delete. Opened from the role's name.
   */
  renderRoleDetail?: (role: RoleView) => ReactNode;
}

const ICON: Record<string, LucideIcon> = {
  organization: Building2,
  court: LayoutGrid,
  booking: CalendarDays,
  activity: Flag,
  tournament: Trophy,
  member: Users,
  messaging: MessageSquare,
  finance: Wallet,
};

const GROUPS: { group: RoleGroup; label: MessageKey; lead: MessageKey }[] = [
  { group: 'both', label: 'saAreas.group.both', lead: 'saAreas.group.both.lead' },
  { group: 'centre', label: 'saAreas.group.centre', lead: 'saAreas.group.centre.lead' },
  { group: 'club', label: 'saAreas.group.club', lead: 'saAreas.group.club.lead' },
];

const DOTS = { 1: '●○○', 2: '●●○', 3: '●●●' } as const;

/** Which cell is open: one area of a role, or the whole role (`'*'`). */
type Open = { roleId: string; areaKey: string } | null;

export function RoleAreaOverview({
  roles,
  rows,
  titleOf,
  canWrite,
  busyRole,
  rowValue,
  isDirty,
  pendingChange,
  onSetCapabilities,
  onToggle,
  onSave,
  renderRoleDetail,
}: RoleAreaOverviewProps) {
  const t = useT();
  const { locale } = useLocale();
  const [open, setOpen] = useState<Open>(null);
  const [query, setQuery] = useState('');
  const areas = areasFor(rows.map((c) => c.key));
  const byKey = new Map(rows.map((c) => [c.key, c] as const));
  const q = query.trim().toLowerCase();
  const shown = q ? roles.filter((r) => r.name.toLowerCase().includes(q)) : roles;

  const standingLabel = (s: AreaStanding) =>
    s.kind === 'none'
      ? t('saAreas.none')
      : s.kind === 'level'
        ? t(s.level.label)
        : t('saAreas.custom', { held: String(s.held), total: String(s.total) });

  const cellClass = (area: Area, s: AreaStanding) => {
    const tone = toneOf(area);
    if (s.kind === 'none') return 'text-ink-3';
    if (s.kind === 'custom') return tone.custom;
    return `${tone.cell[levelStrength(area, s.level) - 1]} font-medium`;
  };

  const areaPanel = (role: RoleView, area: Area, areaCaps: string[]) => {
    const value = rowValue(role);
    const standing = standingIn(area, areaCaps, value);
    const change = pendingChange(role);
    const dirty = isDirty(role);
    const busy = busyRole === role.id;
    const tone = toneOf(area);
    const Icon = ICON[area.key] ?? LayoutGrid;
    const choose = (levelKey: string | null) => {
      const level = levelKey ? (area.levels.find((l) => l.key === levelKey) ?? null) : null;
      onSetCapabilities(role, withLevel(value, areaCaps, level));
    };
    return (
      <div className={`px-4 py-3 border-t border-l-4 ${tone.panel}`} data-testid="sa-areas-panel">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h4
            className={`font-display text-sm font-semibold inline-flex items-center gap-1.5 ${tone.head}`}
          >
            <Icon size={14} aria-hidden />
            {t('saAreas.panel.title', { role: role.name, area: t(area.label) })}
          </h4>
          <button
            type="button"
            className="text-[11px] text-ink-3 hover:text-ink"
            onClick={() => setOpen(null)}
          >
            {t('saAreas.panel.close')}
          </button>
        </div>

        <fieldset className="mt-2" disabled={!canWrite || busy}>
          <legend className="font-mono uppercase tracking-wider text-[10px] text-ink-3">
            {t('saAreas.panel.level')}
          </legend>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {[null, ...area.levels].map((level) => {
              const key = level?.key ?? 'none';
              const active =
                level === null
                  ? standing.kind === 'none'
                  : standing.kind === 'level' && standing.level.key === level.key;
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => choose(level?.key ?? null)}
                  className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                    active
                      ? 'bg-ink text-white border-ink'
                      : 'border-line bg-paper text-ink-2 hover:bg-bg-soft'
                  }`}
                  data-testid={`sa-areas-level-${key}`}
                >
                  {level ? (
                    <>
                      <span aria-hidden className="mr-1 tracking-tighter">
                        {DOTS[levelStrength(area, level)]}
                      </span>
                      {t(level.label)}
                    </>
                  ) : (
                    t('saAreas.none')
                  )}
                </button>
              );
            })}
            {standing.kind === 'custom' && (
              <span className={`rounded-full px-3 py-1 text-xs bg-paper ${tone.custom}`}>
                {standingLabel(standing)}
              </span>
            )}
          </div>
        </fieldset>

        <fieldset className="mt-3" disabled={!canWrite || busy}>
          <legend className="font-mono uppercase tracking-wider text-[10px] text-ink-3">
            {t('saAreas.panel.rights')}
          </legend>
          <ul className="mt-1 grid gap-1 sm:grid-cols-2">
            {areaCaps.map((key) => {
              const cap = byKey.get(key);
              if (!cap) return null;
              const id = `sa-areas-${role.id}-${key}`;
              return (
                <li key={key} className="flex items-start gap-2 rounded-lg px-2 py-1.5 bg-paper">
                  <input
                    id={id}
                    type="checkbox"
                    className="mt-0.5"
                    checked={value.includes(key)}
                    disabled={!canWrite || busy}
                    onChange={() => onToggle(role, key)}
                    data-testid={`sa-areas-cap-${key}`}
                  />
                  <label htmlFor={id} className="text-xs text-ink leading-snug" title={key}>
                    <span className="font-medium">{titleOf(cap)}</span>
                    {cap.description ? (
                      <span className="block text-[11px] text-ink-3">{cap.description}</span>
                    ) : null}
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>

        {canWrite ? (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn-lime text-xs"
              disabled={!dirty || busy}
              onClick={() => onSave(role)}
              data-testid="sa-areas-save"
            >
              {busy ? t('saAreas.panel.saving') : t('saAreas.panel.save')}
            </button>
            {dirty && (
              <span className="text-[11px] text-ink-3" data-testid="sa-areas-pending">
                {t('saAreas.panel.pending', {
                  added: String(change.added.length),
                  removed: String(change.removed.length),
                })}
              </span>
            )}
          </div>
        ) : (
          <p className="mt-3 text-[11px] text-ink-3">{t('saAreas.panel.readonly')}</p>
        )}
      </div>
    );
  };

  const roleRow = (role: RoleView) => {
    const value = rowValue(role);
    const openHere = open?.roleId === role.id ? open : null;
    const openArea =
      openHere && openHere.areaKey !== '*'
        ? areas.find((a) => a.area.key === openHere.areaKey)
        : null;
    const wholeRole = openHere?.areaKey === '*';
    return (
      <Fragment key={role.id}>
        <tr className="border-b border-line-soft" data-testid={`sa-areas-row-${role.key}`}>
          <th scope="row" className="px-3 py-2 text-left align-top">
            {/* THE ROLE'S NAME OPENS EVERYTHING ABOUT IT (3A). A role name is
                DATA, and Norwegian whatever the screen's language, so it is
                declared so and hyphenates by Norwegian rules (#981). */}
            {renderRoleDetail ? (
              <button
                type="button"
                aria-expanded={wholeRole}
                onClick={() => setOpen(wholeRole ? null : { roleId: role.id, areaKey: '*' })}
                className="text-left hover:underline decoration-dotted"
                title={t('saAreas.role.open', { role: role.name })}
                data-testid={`sa-areas-role-${role.key}`}
              >
                <span
                  lang="nb"
                  className="block text-xs font-semibold text-ink hyphens-auto break-words"
                >
                  {role.name}
                </span>
              </button>
            ) : (
              <span
                lang="nb"
                className="block text-xs font-semibold text-ink hyphens-auto break-words"
              >
                {role.name}
              </span>
            )}
            {!role.isSystem && (
              <span className="block text-[10px] text-ink-3">{t('saAreas.role.custom')}</span>
            )}
            {isDirty(role) && (
              <span className="text-[10px] text-lime-deep">{t('saAreas.unsaved')}</span>
            )}
          </th>
          {areas.map(({ area, caps }) => {
            const standing = standingIn(area, caps, value);
            const active = openHere?.areaKey === area.key;
            return (
              <td key={area.key} className="px-1 py-1.5 align-top">
                <button
                  type="button"
                  aria-expanded={active}
                  onClick={() => setOpen(active ? null : { roleId: role.id, areaKey: area.key })}
                  // THE WHOLE NAME, ALWAYS (#979): it may break at a
                  // hyphenation point rather than be clipped.
                  className={`w-full rounded-md px-1 py-1 text-left text-[10px] leading-tight hyphens-auto [overflow-wrap:anywhere] ${cellClass(
                    area,
                    standing,
                  )} ${active ? 'ring-2 ring-ink' : ''}`}
                  title={t('saAreas.cellTitle', { role: role.name, area: t(area.label) })}
                  data-testid={`sa-areas-cell-${role.key}-${area.key}`}
                >
                  {standing.kind === 'level' && (
                    <span aria-hidden className="block tracking-tighter opacity-80">
                      {DOTS[levelStrength(area, standing.level)]}
                    </span>
                  )}
                  {standing.kind === 'none' ? (
                    <>
                      <span aria-hidden>–</span>
                      <span className="sr-only">{t('saAreas.none')}</span>
                    </>
                  ) : (
                    standingLabel(standing)
                  )}
                </button>
              </td>
            );
          })}
        </tr>
        {openHere && openArea && (
          <tr>
            <td colSpan={areas.length + 1} className="p-0">
              {areaPanel(role, openArea.area, openArea.caps)}
            </td>
          </tr>
        )}
        {wholeRole && renderRoleDetail && (
          <tr>
            <td colSpan={areas.length + 1} className="p-0" data-testid="sa-areas-role-detail">
              <div className="flex items-baseline justify-between gap-2 px-4 pt-3 border-t border-line">
                <h4 className="font-display text-sm font-semibold text-ink">
                  {t('saAreas.role.allRights', { role: role.name })}
                </h4>
                <button
                  type="button"
                  className="text-[11px] text-ink-3 hover:text-ink"
                  onClick={() => setOpen(null)}
                >
                  {t('saAreas.panel.close')}
                </button>
              </div>
              {renderRoleDetail(role)}
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  return (
    <div data-testid="sa-areas">
      {/* SEARCH AND THE KEY, on one line. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pt-3">
        <label className="relative flex-1 min-w-[12rem]">
          <span className="sr-only">{t('saAreas.search')}</span>
          <Search
            size={13}
            aria-hidden
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('saAreas.search')}
            // `.input` sets its own padding after the utilities, so the room for the
            // icon is marked important — without it the glass sat on the text.
            className="input w-full !pl-8 text-xs"
            data-testid="sa-areas-search"
          />
        </label>
        <p className="text-[11px] text-ink-3" data-testid="sa-areas-legend">
          {t('saAreas.legend', { one: DOTS[1], two: DOTS[2], three: DOTS[3] })}
        </p>
      </div>
      {/* TABLE-FIXED: the columns share the width the card has. A new role is
          a new ROW; a new area would be a new column, and there are eight.
          LANG FOLLOWS THE LOCALE (#993): the headings and levels are
          translated, so they are in the language the screen is in. */}
      <table
        className="mt-2 w-full table-fixed border-collapse text-[11px]"
        lang={INTL_TAG[locale]}
      >
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className="w-32 px-3 py-2 text-left font-mono uppercase text-ink-3">
              {t('saAreas.role')}
            </th>
            {areas.map(({ area }) => {
              const Icon = ICON[area.key] ?? LayoutGrid;
              return (
                <th
                  key={area.key}
                  scope="col"
                  className={`px-1 py-2 text-left text-[10px] font-semibold leading-tight align-bottom hyphens-auto [overflow-wrap:anywhere] ${toneOf(area).head}`}
                  data-testid={`sa-areas-head-${area.key}`}
                >
                  <Icon size={14} aria-hidden className="block mb-1" />
                  {t(area.label)}
                </th>
              );
            })}
          </tr>
        </thead>
        {GROUPS.map(({ group, label, lead }) => {
          const inGroup = shown.filter((r) => groupOf(r.appliesTo) === group);
          // A group with nobody in it says so — but not while searching, where
          // an empty group is just a group the search did not match.
          if (inGroup.length === 0 && q) return null;
          return (
            <tbody key={group} data-testid={`sa-areas-group-${group}`}>
              <tr>
                <th
                  scope="rowgroup"
                  colSpan={areas.length + 1}
                  className="px-3 pt-4 pb-1.5 text-left bg-bg-soft/60 border-b border-line"
                >
                  <span className="text-xs font-semibold text-ink">{t(label)}</span>
                  <span className="ml-2 text-[11px] font-normal text-ink-3">
                    {inGroup.length === 0 ? t('saAreas.group.empty') : t(lead)}
                  </span>
                </th>
              </tr>
              {inGroup.map(roleRow)}
            </tbody>
          );
        })}
      </table>
      {q && shown.length === 0 && (
        <p className="px-4 py-3 text-xs text-ink-3" data-testid="sa-areas-no-match">
          {t('saAreas.search.none')}
        </p>
      )}
    </div>
  );
}

/** A section of the one-page view: a heading, a line, and its content. */
export function AreaSection({
  title,
  lead,
  testId,
  empty = false,
  children,
}: {
  title: MessageKey;
  lead: MessageKey;
  testId: string;
  /** Nothing to show yet — said in words, not drawn as an empty table. */
  empty?: boolean;
  children: ReactNode;
}) {
  const t = useT();
  return (
    <section
      className="rounded-xl border border-line bg-paper overflow-hidden"
      data-testid={testId}
    >
      <div className="px-4 py-3 border-b border-line bg-bg-soft/60">
        <h3 className="font-display text-sm font-semibold text-ink">{t(title)}</h3>
        <p className="text-[11px] text-ink-3">{t(lead)}</p>
      </div>
      {empty ? (
        <p className="px-4 py-3 text-xs text-ink-3">{t('saAreas.section.empty')}</p>
      ) : (
        children
      )}
    </section>
  );
}

/**
 * The quiet way between the overview and the old tabbed grid. Not a switch at
 * the top any more (3A, 2026-10-20) — a link at the end of the one, and back
 * at the start of the other.
 */
export function AreaViewLink({
  to,
  onView,
}: {
  to: 'overview' | 'detail';
  onView: (v: 'overview' | 'detail') => void;
}) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={() => onView(to)}
      className="text-[11px] text-ink-3 underline decoration-dotted hover:text-ink"
      data-testid={`sa-areas-view-${to}`}
    >
      {to === 'detail' ? t('saAreas.view.toDetail') : t('saAreas.view.toOverview')}
    </button>
  );
}
